import {type ReactNode, useEffect, useRef, useState} from 'react';
import {Search} from 'lucide-react';
import {
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput
} from '@kroll-resolver-crisp/ui-components-react';
import {ACTIVE_FILTER_TRIGGER} from '@/lib/filterTrigger';
import {
  FILTER_BAR_NARROW_HIDE_WHEN_SEARCH_OPEN,
  FILTER_BAR_NARROW_SEARCH_BUTTON,
  FILTER_BAR_NARROW_SEARCH_INPUT,
  FILTER_BAR_NARROW_SEARCH_OPEN
} from '@/lib/filterBar';
import {cn} from '@/lib/utils';

// The item grid's filter bar: one non-wrapping row of `leading`, the search, then `children`. It
// responds to its own width (see lib/filterBar.ts) - the controls inside carry the compact break,
// and this owns the narrow one, where the search becomes a button that swaps the whole row for a
// full-width box. Enter, Esc or leaving the box puts the row back; the text stays applied.
export function FilterBar({
  search,
  className,
  leading,
  children
}: {
  readonly search?:
    | undefined
    | {
        readonly value: string;
        readonly onChange: (value: string) => void;
        readonly placeholder: string;
        // Width of the inline box when the bar is wide enough to show it.
        readonly className?: string;
      };
  readonly className?: string;
  readonly leading?: ReactNode;
  readonly children?: ReactNode;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) {
      inputRef.current?.focus();
    }
  }, [searchOpen]);

  return (
    <div
      className={cn('group/bar @container', className)}
      data-search-open={searchOpen ? '' : undefined}>
      <div className="flex flex-nowrap items-center gap-2">
        {search ? (
          <InputGroup className={cn('w-full', FILTER_BAR_NARROW_SEARCH_OPEN)}>
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput
              ref={inputRef}
              placeholder={search.placeholder}
              aria-label={search.placeholder}
              value={search.value}
              onChange={(event) => {
                search.onChange(event.target.value);
              }}
              onBlur={() => {
                setSearchOpen(false);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === 'Escape') {
                  event.preventDefault();
                  // Esc would otherwise also close a dialog or sheet the bar sits in.
                  event.stopPropagation();
                  setSearchOpen(false);
                }
              }}
            />
          </InputGroup>
        ) : null}

        <div
          className={cn(
            'flex min-w-0 flex-1 flex-nowrap items-center gap-2',
            FILTER_BAR_NARROW_HIDE_WHEN_SEARCH_OPEN
          )}>
          {leading}
          {search ? (
            <>
              <InputGroup
                className={cn('w-56 max-w-full', search.className, FILTER_BAR_NARROW_SEARCH_INPUT)}>
                <InputGroupAddon>
                  <Search />
                </InputGroupAddon>
                <InputGroupInput
                  placeholder={search.placeholder}
                  aria-label={search.placeholder}
                  value={search.value}
                  onChange={(event) => {
                    search.onChange(event.target.value);
                  }}
                />
              </InputGroup>
              <Button
                variant="outline"
                size="icon-sm"
                aria-label={search.value ? `${search.placeholder} (active)` : search.placeholder}
                title={search.placeholder}
                className={cn(
                  'shrink-0',
                  FILTER_BAR_NARROW_SEARCH_BUTTON,
                  search.value !== '' && ACTIVE_FILTER_TRIGGER
                )}
                onClick={() => {
                  setSearchOpen(true);
                }}>
                <Search />
              </Button>
            </>
          ) : null}
          {children}
        </div>
      </div>
    </div>
  );
}
